import { Suspense, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import { useCreateOrUpdatePermission, usePermissionFormHandler } from './hooks';
import { useGetPermissionContent } from '@/api/permission/permission';
import { PermissionContentTypeSchema } from '@/zod_schemas';
import AssignPermissionWithSuspense from './AssignPermission';
import CreateEditPermission from './Form/CreateEditPermission';
import PageHeader from '@/components/PageHeader/PageHeader';
import LoadingState from '@/components/LoadingState';

const PermissionsForm = () => {
  const {
    handleSubmit,
    register,
    getValues,
    mode,
    permissionId,
    formState: { errors },
  } = usePermissionFormHandler();

  const pageStatus = mode === 'create' ? 'Create' : 'Edit';

  const { data } = useGetPermissionContent();

  const [permissionGroups, setPermissionGroups] = useState<
    PermissionContentTypeSchema[]
  >([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (data) {
      // Initialize `allowed` state for each permission based on getValues().permissions
      const initialPermissions = data.map((group) => ({
        ...group,
        permissions: group.permissions.map((permission) => ({
          ...permission,
          allowed:
            mode !== 'create'
              ? getValues().permissions!.includes(permission.id)
              : false, // Set allowed based on formData
        })),
      }));
      setPermissionGroups(initialPermissions);
    }
  }, [data, getValues, mode]);

  const handleTogglePermission = (
    groupIndex: number,
    permissionIndex: number,
  ) => {
    const updatedGroups = [...permissionGroups];
    const currentPermission =
      updatedGroups[groupIndex].permissions[permissionIndex];
    currentPermission.allowed = !currentPermission.allowed;
    setPermissionGroups(updatedGroups);
  };

  const handleSelectAll = (groupIndex: number) => {
    const updatedGroups = [...permissionGroups];
    const allAllowed = updatedGroups[groupIndex].permissions.every(
      (perm) => perm.allowed,
    );
    updatedGroups[groupIndex].permissions = updatedGroups[
      groupIndex
    ].permissions.map((perm) => ({
      ...perm,
      allowed: !allAllowed,
    }));
    setPermissionGroups(updatedGroups);
  };

  const onSubmit = useCreateOrUpdatePermission(
    mode,
    permissionGroups,
    permissionId,
  );

  const handleListItemClick = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
  ) => {
    setSelectedIndex(index);
  };

  return (
    <Box
      sx={{ width: '100%', maxWidth: 1680, mx: 'auto', p: { xs: 2, md: 3 } }}
    >
      <PageHeader
        eyebrow="Administration"
        title={`${pageStatus} permission group`}
        description="Configure permissions and, for existing groups, manage the users assigned to them."
        compact
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <List component="nav" aria-label="Permission group sections">
              <ListItemButton
                selected={selectedIndex === 0}
                onClick={(event) => handleListItemClick(event, 0)}
                sx={{
                  borderRadius: 1,
                  position: 'relative',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    bgcolor:
                      selectedIndex === 0 ? 'primary.main' : 'transparent',
                    borderRadius: '8px 0 0 8px',
                  },
                }}
              >
                <ListItemIcon>
                  <EditIcon
                    color={selectedIndex === 0 ? 'primary' : 'inherit'}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Permission rules"
                  secondary={`${pageStatus} permission group`}
                  primaryTypographyProps={{
                    fontWeight: selectedIndex === 0 ? 'bold' : 'normal',
                  }}
                />
              </ListItemButton>
              {pageStatus === 'Edit' && (
                <ListItemButton
                  selected={selectedIndex === 1}
                  onClick={(event) => handleListItemClick(event, 1)}
                  sx={{
                    borderRadius: 1,
                    position: 'relative',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      bgcolor:
                        selectedIndex === 1 ? 'primary.main' : 'transparent',
                      borderRadius: '8px 0 0 8px',
                    },
                  }}
                >
                  <ListItemIcon>
                    <GroupIcon
                      color={selectedIndex === 1 ? 'primary' : 'inherit'}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Users"
                    secondary="Assign or remove users"
                    primaryTypographyProps={{
                      fontWeight: selectedIndex === 1 ? 'bold' : 'normal',
                    }}
                  />
                </ListItemButton>
              )}
            </List>
          </Paper>
        </Grid>
        {/* Additional Cards for permission/users, etc. */}
        <Grid item xs={12} md={9}>
          {selectedIndex === 0 ? (
            <CreateEditPermission
              register={register}
              permissionGroups={permissionGroups}
              errors={errors}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              handleSelectAll={handleSelectAll}
              handleTogglePermission={handleTogglePermission}
            />
          ) : (
            <AssignPermissionWithSuspense />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const PermissionWithSuspense = () => (
  <Suspense fallback={<LoadingState label="Loading permission group…" />}>
    <PermissionsForm />
  </Suspense>
);

export default PermissionWithSuspense;
